import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getHumanErrorMessage } from "@/lib/humanErrorMessages";
import { getSafeExternalUrl, openExternalUrl } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { logDebug, logError } from "@/lib/logger";
import { RealtimeChannel } from "@supabase/supabase-js";

interface ApplyAttemptChange {
  id: string;
  job_id: string;
  status:
    | "pending"
    | "processing"
    | "success"
    | "applied"
    | "failed"
    | "terminal_failed"
    | "blocked"
    | "assisted"
    | "cancelled"
    | "skipped";
  error_reason: string | null;
  status_reason_code?: string | null;
  failure_reason?: string | null;
  completed_at: string | null;
}

interface UseApplyAttemptRealtimeOptions {
  onSuccess?: (attemptId: string, jobId: string) => void;
  onFailure?: (attemptId: string, jobId: string, reason: string | null) => void;
  onProcessing?: (attemptId: string, jobId: string) => void;
  /** Called when failure is due to CAPTCHA/login - suggest assisted mode */
  onAssistedFallback?: (attemptId: string, jobId: string, applyUrl: string | null) => void;
  /** Called when failure needs manual fallback - show zero-friction dialog */
  onManualFallback?: (attemptId: string, jobId: string, applyUrl: string | null, errorReason: string | null) => void;
}

// Failure reasons that should trigger assisted mode fallback
const ASSISTED_FALLBACK_REASONS = [
  'captcha',
  'hcaptcha',
  'recaptcha',
  'login_required',
  'requires_login',
  'authentication',
  'session_expired',
  'blocked',
  'bot_detected',
];

/**
 * Check if failure reason should trigger assisted mode fallback
 */
function shouldFallbackToAssisted(reason: string | null): boolean {
  if (!reason) return false;
  const lowerReason = reason.toLowerCase();
  return ASSISTED_FALLBACK_REASONS.some(r => lowerReason.includes(r));
}

/**
 * Real-time subscription to apply_attempts changes
 * Shows toast notifications when applications succeed or fail
 * Triggers assisted mode fallback for CAPTCHA/login failures
 */
export function useApplyAttemptRealtime(options: UseApplyAttemptRealtimeOptions = {}) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const userIdRef = useRef<string | null>(null);
  // Track which attempts we've already notified about to avoid duplicates
  const notifiedRef = useRef<Set<string>>(new Set());

  const handleChange = useCallback(async (payload: { new: ApplyAttemptChange; old: ApplyAttemptChange | null }) => {
    const newAttempt = payload.new;
    const oldAttempt = payload.old;
    
    // Only react to status changes
    if (!oldAttempt || oldAttempt.status === newAttempt.status) {
      return;
    }

    // Create a unique key for this notification
    const notificationKey = `${newAttempt.id}-${newAttempt.status}`;
    
    // Avoid duplicate notifications
    if (notifiedRef.current.has(notificationKey)) {
      return;
    }
    notifiedRef.current.add(notificationKey);
    
    // Clean up old notification keys (keep last 100)
    if (notifiedRef.current.size > 100) {
      const keys = Array.from(notifiedRef.current);
      keys.slice(0, keys.length - 100).forEach(k => notifiedRef.current.delete(k));
    }

    // Handle different status transitions
    const fetchFallbackContext = async () => {
      let applyUrl: string | null = null;

      try {
        const { data: job } = await supabase
          .from("aggregated_jobs")
          .select("apply_url, url")
          .eq("id", newAttempt.job_id)
          .maybeSingle();
        applyUrl = job?.apply_url || job?.url || null;
      } catch {
        // tyst
      }

      return {
        applyUrl: getSafeExternalUrl(applyUrl),
      };
    };

    switch (newAttempt.status) {
      case "pending":
        // Ansökan är köad - visa tydlig feedback
        toast({
          title: "Ansökan köad",
          description: "Din ansökan kommer att skickas automatiskt snart.",
          duration: 3000,
        });
        options.onProcessing?.(newAttempt.id, newAttempt.job_id);
        break;
        
      case "processing":
        // Ansökan skickas nu - proaktiv kommunikation
        toast({
          title: "Skickar ansökan...",
          description: "Vi fyller i formuläret och skickar nu.",
          duration: 3000,
        });
        options.onProcessing?.(newAttempt.id, newAttempt.job_id);
        break;
        
      case "success":
      case "applied":
        // SUCCESS! Show success toast med mer information
        // Hämta jobb-info för bättre feedback
        supabase
          .from("aggregated_jobs")
          .select("title, company")
          .eq("id", newAttempt.job_id)
          .maybeSingle()
          .then(({ data: job }) => {
            const jobInfo = job ? ` till ${job.company}` : "";
            toast({
              title: "Ansökan skickad! ✓",
              description: `Din ansökan${jobInfo} har skickats framgångsrikt.`,
              duration: 5000,
            });
          })
          .then(undefined, () => {
            // Fallback om jobb-info inte kan hämtas
            toast({
              title: "Ansökan skickad! ✓",
              description: "Din ansökan har skickats framgångsrikt.",
              duration: 5000,
            });
          });
        options.onSuccess?.(newAttempt.id, newAttempt.job_id);
        break;
        
      case "failed":
      case "terminal_failed":
      case "blocked":
      case "cancelled": {
        const reasonCode =
          newAttempt.status_reason_code ||
          newAttempt.failure_reason ||
          newAttempt.error_reason ||
          "unknown";
        const human = getHumanErrorMessage(reasonCode);
        const reasonText = newAttempt.error_reason || String(reasonCode);

        // Hämta jobb-info och apply_url för fallback
        const { applyUrl: safeApplyUrl } = await fetchFallbackContext();

        // CAPTCHA/login – erbjud manuell/assisterad väg
        if (shouldFallbackToAssisted(newAttempt.status_reason_code || newAttempt.error_reason)) {
          toast({
            title: "Behöver din hjälp",
            description: human.description + (human.action ? " " + human.action : ""),
            variant: "destructive",
            duration: 6000,
          });
          options.onAssistedFallback?.(newAttempt.id, newAttempt.job_id, safeApplyUrl);
          
          // Visa zero-friction manual fallback dialog
          if (options.onManualFallback && safeApplyUrl) {
            options.onManualFallback(newAttempt.id, newAttempt.job_id, safeApplyUrl, reasonText);
          }
        } else {
          // Andra fel - visa tydligt meddelande + erbjud manual fallback
          toast({
            title: "Ansökan skickades inte",
            description: human.description + (human.action ? " " + human.action : ""),
            variant: "destructive",
            duration: 6000,
          });
          
          // För fixable errors, visa zero-friction manual fallback
          const isFixable = reasonCode.toLowerCase().includes("missing") ||
                           reasonCode.toLowerCase().includes("timeout") ||
                           reasonCode.toLowerCase().includes("connection");
          
          if (options.onManualFallback && safeApplyUrl && isFixable) {
            options.onManualFallback(newAttempt.id, newAttempt.job_id, safeApplyUrl, reasonText);
          }
        }
        options.onFailure?.(newAttempt.id, newAttempt.job_id, reasonText);
        break;
      }

      case "skipped": {
        const skipReason =
          newAttempt.status_reason_code ||
          newAttempt.failure_reason ||
          newAttempt.error_reason ||
          "platform_not_supported";
        const human = getHumanErrorMessage(skipReason);
        const { applyUrl: safeApplyUrl } = await fetchFallbackContext();
        toast({
          title: "Ansökan skickades inte",
          description: human.description + (human.action ? " " + human.action : ""),
          variant: "destructive",
        });
        if (options.onManualFallback && safeApplyUrl) {
          options.onManualFallback(newAttempt.id, newAttempt.job_id, safeApplyUrl, newAttempt.error_reason || String(skipReason));
        }
        options.onFailure?.(newAttempt.id, newAttempt.job_id, newAttempt.error_reason || String(skipReason));
        break;
      }

      case "assisted": {
        const assistedReason =
          newAttempt.status_reason_code ||
          newAttempt.failure_reason ||
          newAttempt.error_reason ||
          "captcha_blocked";
        const human = getHumanErrorMessage(assistedReason);
        const { applyUrl: safeApplyUrl } = await fetchFallbackContext();

        toast({
          title: "Behöver din hjälp",
          description: human.description + (human.action ? " " + human.action : ""),
          variant: "destructive",
          duration: 6000,
        });

        if (options.onAssistedFallback) {
          options.onAssistedFallback(newAttempt.id, newAttempt.job_id, safeApplyUrl);
        }
        if (options.onManualFallback && safeApplyUrl) {
          options.onManualFallback(newAttempt.id, newAttempt.job_id, safeApplyUrl, newAttempt.error_reason || String(assistedReason));
        }
        options.onFailure?.(newAttempt.id, newAttempt.job_id, newAttempt.error_reason || String(assistedReason));
        break;
      }
    }
  }, [options]);

  useEffect(() => {
    let mounted = true;

    const setupSubscription = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || !mounted) return;
      
      userIdRef.current = session.user.id;

      // Clean up existing subscription
      if (channelRef.current) {
        await channelRef.current.unsubscribe();
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      // Subscribe to apply_attempts changes for this user
      const channel = supabase
        .channel(`apply_attempts_${session.user.id}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "apply_attempts",
            filter: `user_id=eq.${session.user.id}`,
          },
          (payload) => {
            if (mounted && payload.new && payload.old) {
              handleChange({
                new: payload.new as ApplyAttemptChange,
                old: payload.old as ApplyAttemptChange,
              });
            }
          }
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            logDebug("[Realtime] Subscribed to apply_attempts changes");
          } else if (status === "CHANNEL_ERROR") {
            logError("[Realtime] Error subscribing to apply_attempts");
          }
        });

      channelRef.current = channel;
    };

    setupSubscription();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) {
        setupSubscription();
      } else if (event === "SIGNED_OUT") {
        if (channelRef.current) {
          const channelToRemove = channelRef.current;
          channelRef.current = null;
          channelToRemove.unsubscribe().then(() => {
            supabase.removeChannel(channelToRemove);
          }).catch(() => {
            // Ignore errors during cleanup
          });
        }
        userIdRef.current = null;
        notifiedRef.current.clear();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (channelRef.current) {
        const channelToRemove = channelRef.current;
        channelRef.current = null;
        channelToRemove.unsubscribe().then(() => {
          // Remove channel from Supabase client to prevent memory leaks
          supabase.removeChannel(channelToRemove);
        }).catch(() => {
          // Ignore errors during cleanup
        });
      }
    };
  }, [handleChange]);

  return {
    // Expose for testing/debugging
    isSubscribed: () => channelRef.current !== null,
  };
}